import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import type { Dayjs } from "dayjs";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { AI_HUB_DEFAULT_PAGE_SIZE } from "../constants";
import TriageResultFilterBar from "./TriageResultFilterBar";
import TriageResultTable from "./TriageResultTable";
import {
  fetchTriageResultPage,
  listTriageResults,
} from "./triageResultData";

export default function TriageResultTab() {
  const [objectType, setObjectType] = useState("");
  const [objectSubType, setObjectSubType] = useState("");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: AI_HUB_DEFAULT_PAGE_SIZE,
  });
  const { planTypesLoading } = useAssessmentPlanFilterOptions();

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [fromDate, objectSubType, objectType, toDate]);

  const filter = useMemo(
    () => ({ objectType, objectSubType, fromDate, toDate }),
    [fromDate, objectSubType, objectType, toDate],
  );

  const exportRows = useMemo(() => listTriageResults(filter), [filter]);

  const { rows, total } = useMemo(
    () =>
      fetchTriageResultPage({
        ...filter,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    [filter, paginationModel.page, paginationModel.pageSize],
  );

  const handleDateRangeChange = (from: Dayjs | null, to: Dayjs | null) => {
    setFromDate(from);
    setToDate(to);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
      }}
    >
      <TriageResultFilterBar
        objectType={objectType}
        objectSubType={objectSubType}
        fromDate={fromDate}
        toDate={toDate}
        onObjectTypeChange={(nextObjectType, nextObjectSubType) => {
          setObjectType(nextObjectType);
          setObjectSubType(nextObjectSubType);
        }}
        onObjectSubTypeChange={setObjectSubType}
        onDateRangeChange={handleDateRangeChange}
      />
      {planTypesLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TriageResultTable
          rows={rows}
          rowCount={total}
          exportRows={exportRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      )}
    </Box>
  );
}
